import { useEffect, useMemo, useState } from 'react'
import { Betrag } from './Betrag'
import { api, betrag, periodenname, tagesdatum } from './api'
import { kontoname } from './Auswahlfeld'
import { fachlicherSatz } from './Fehlersatz'
import { Agenda } from './Agenda'
import type { Agendaeintrag } from './Agenda'
import { Druckknopf, useDruckvermerk } from './Druck'
import { ganz, ladeMappe, text, zahl } from './Mappe'
import type { Mappenauftrag, Mappenzelle } from './Mappe'
import { schrittwert } from './Vorgaenge'
import {
  aktuelleSprache,
  anteil,
  beschriftung,
  bewegungsartname,
  herleitungsname,
  kurswert,
  regelname,
  satz,
  t,
  anzahlwort,
} from './sprache'
import type { Sprachschluessel } from './sprache'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import type {
  Grundsatz,
  Lehrfallbericht,
  Lehrfallschritt,
  Lehrfallzeile,
  Regelnachweis,
} from './typen'

/** Die Kennung eines Kapitels — dieselbe in der Agenda wie am Kapitel selbst. */
const KAPITEL = {
  grundsaetze: 'pruefer-grundsaetze',
  ungeprueft: 'pruefer-ungeprueft',
  lehrfall: 'pruefer-lehrfall',
  latenteSteuern: 'pruefer-latente-steuern',
}

/** Das Kapitel einer Konsolidierungsart. */
const schrittkennung = (code: string) => `pruefer-schritt-${code}`

/**
 * Der Name eines Schritts kommt aus dem Rechenkern, nicht aus der Sprachdatei.
 *
 * Zweite Ebene der Mehrsprachigkeit (ADR 010): Oberflächentexte in
 * Sprachdateien, fachliche Bezeichnungen mehrsprachig aus der Quelle. Stünde
 * der Name hier ein zweites Mal, hieße derselbe Schritt im Lauf anders als im
 * Beispielkonzern.
 */
function schrittname(schritt: Lehrfallschritt): string {
  return aktuelleSprache() === 'de' ? schritt.nameDe : schritt.nameEn || schritt.nameDe
}

/**
 * Die Prüferansicht: der Regelnachweis (2.13 Abschnitt 9).
 *
 * **Zwei Teile, und beide beantworten dieselbe Frage auf verschiedene Weise:**
 * Läuft hier alles ordnungsgemäß? Der erste mißt die Grundsätze am eigenen
 * Bestand — jeden Beleg, jeden gerechneten Abschluß, jedes Konto. Der zweite
 * rechnet einen erfundenen Beispielkonzern durch alle Konsolidierungsarten
 * vor.
 *
 * **Nichts wird abgehakt** (R805). Der Bildschirm zeigt, was das System eben
 * gerechnet hat; er speichert nichts. Ein gesetzter Haken verfiele mit dem
 * nächsten Softwarestand, und was er dann noch behauptet, wüßte niemand.
 *
 * **Die Zahlen im Beispiel sind nicht geschrieben** (R806). Sie entstehen bei
 * jedem Aufruf im selben Rechenkern wie jeder Kundenabschluß — sonst bliebe
 * das Beispiel richtig, auch wenn die Maschine falsch rechnete.
 */
export function Prueferansicht() {
  const [bericht, setBericht] = useState<Regelnachweis | null>(null)
  const [fehler, setFehler] = useState(false)
  /** Was dazu am Ort steht — nur ein fachlicher Satz (7.2 Abschnitt 30, R1003/R1004). */
  const [fehlersatz, setFehlersatz] = useState<string | null>(null)

  useEffect(() => {
    api
      .prueferansicht()
      .then(setBericht)
      .catch((f) => {
        setFehler(true)
        setFehlersatz(fachlicherSatz(f))
      })
  }, [])

  /*
    **Der Prüfcode steht im Fuß jeder Druckseite** (R811, R813). Er wird hier
    angemeldet und nicht in die Fußzeile geschrieben: Ein herausgetrenntes
    Blatt muß zuordenbar bleiben, und welcher Code gilt, weiß der Bericht.
  */
  useDruckvermerk(bericht?.lehrfall.pruefcode ?? null)

  // Eine Störung steht im Störungsfenster und nicht noch einmal hier (R1003).
  if (fehler) {
    return fehlersatz ? <p className="hinweis">{fehlersatz}</p> : null
  }

  if (!bericht) {
    return <p className="hinweis">{t('vorgang.laedt')}</p>
  }

  return (
    <>
      <p className="erlaeuterung">{t('pruefer.einleitung')}</p>

      {/*
        **Die zwei Wege aus dem Bildschirm heraus.** Sie stehen oben und nicht
        unten: Wer den Bericht in die Akte nehmen will, entscheidet das, bevor
        er ihn liest, und nicht nach vierzehn Kapiteln.
      */}
      <div className="knopfreihe nur-bildschirm">
        <Druckknopf />
        <Mappenknopf bericht={bericht} />
      </div>

      <Agenda eintraege={agenda(bericht)} />

      <Kapitel id={KAPITEL.grundsaetze} titel={t('pruefer.grundsaetze')}>
        <p className="erlaeuterung">{t('pruefer.grundsaetze.erlaeuterung')}</p>

        <div className="zusagenliste">
          {bericht.grundsaetze.map((g) => (
            <Grundsatzzeile key={g.schluessel} grundsatz={g} />
          ))}
        </div>
      </Kapitel>

      <Kapitel id={KAPITEL.ungeprueft} titel={t('pruefer.ungeprueft')}>
        <p className="erlaeuterung">{t('pruefer.ungeprueft.erlaeuterung')}</p>
        <ul className="erlaeuterung">
          {bericht.ungeprueft.map((u) => (
            <li key={u.schluessel}>
              {/* Ohne Regelnummern (7.2 Abschnitt 34, R1133 — Entscheidung vom 2026-10-06). */}
              {t(`pruefer.ungeprueft.${u.schluessel}` as Sprachschluessel)}
            </li>
          ))}
        </ul>
      </Kapitel>

      <Kapitel id={KAPITEL.lehrfall} titel={t('pruefer.lehrfall')}>
        <Beispielkonzern lehrfall={bericht.lehrfall} />
      </Kapitel>
    </>
  )
}

/**
 * Die vierzehn Einträge der Agenda, gestuft (R812).
 *
 * **Oben die drei Abschnitte, darunter jede Konsolidierungsart einzeln.** Der
 * Preis ist benannt — die Agenda ist selbst eine Liste, die gelesen werden
 * will —, und er ist der kleinere: Der Beispielkonzern ist der längste Teil
 * des Berichts, und ohne Einzeleinträge führte in ihn kein Sprung.
 */
function agenda(bericht: Regelnachweis): Agendaeintrag[] {
  return [
    { id: KAPITEL.grundsaetze, text: t('pruefer.grundsaetze') },
    { id: KAPITEL.ungeprueft, text: t('pruefer.ungeprueft') },
    { id: KAPITEL.lehrfall, text: t('pruefer.lehrfall') },
    ...bericht.lehrfall.schritte.map((s) => ({
      id: schrittkennung(s.code),
      text: schrittname(s),
      untergeordnet: true,
    })),
    { id: KAPITEL.latenteSteuern, text: t('pruefer.latente-steuern'), untergeordnet: true },
  ]
}

/**
 * Ein Kapitel des Berichts: aufklappbar, mit einer Kennung, zu der die Agenda
 * springt.
 *
 * **Offen beim Öffnen des Bildschirms.** Ein Bericht, der zugeklappt beginnt,
 * sieht aus wie ein Inhaltsverzeichnis; wer überblicken will, klappt mit einem
 * Handgriff alles zu.
 */
function Kapitel({
  id,
  titel,
  children,
}: {
  id: string
  titel: string
  children: React.ReactNode
}) {
  return (
    <details className="kapitel" id={id} open>
      <summary>
        <h2>{titel}</h2>
      </summary>
      <div className="kapitelinhalt">{children}</div>
    </details>
  )
}

/**
 * Holt die Mappe für die Prüfungsakte (2.13 Abschnitt 9.11).
 *
 * **Der Knopf sperrt sich, solange er arbeitet.** Zweimal geklickt ergäbe
 * zwei Dateien mit demselben Namen im Ordner „Downloads" und keinen Hinweis,
 * welche die neuere ist.
 */
function Mappenknopf({ bericht }: { bericht: Regelnachweis }) {
  const [laeuft, setLaeuft] = useState(false)
  /** Nur ein fachlicher Satz; eine Störung meldet das Störungsfenster (R1003/R1004). */
  const [fehler, setFehler] = useState<string | null>(null)

  async function hole() {
    setLaeuft(true)
    setFehler(null)
    try {
      await ladeMappe(mappe(bericht))
    } catch (f) {
      setFehler(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <>
      <button type="button" disabled={laeuft} onClick={() => void hole()}>
        {t(laeuft ? 'pruefer.mappe.laeuft' : 'pruefer.mappe.knopf')}
      </button>
      {fehler && <span className="hinweis">{fehler}</span>}
    </>
  )
}

/**
 * Ein Grundsatz mit Ampel, Satz und Grundgesamtheit.
 *
 * **Die Zahl steht im Satz und nicht daneben** (R809): „Alle 74 Belege gehen
 * auf" ist eine Auskunft, „in Ordnung" ist keine.
 */
function Grundsatzzeile({ grundsatz }: { grundsatz: Grundsatz }) {
  const ampel =
    grundsatz.urteil === 'gut'
      ? 'gut'
      : grundsatz.urteil === 'warn'
        ? 'warn'
        : grundsatz.urteil === 'schlecht'
          ? 'schlecht'
          : ''

  return (
    <div className="zusage">
      <h3>
        <span className={`ampel ${ampel}`} />
        {t(`pruefer.grundsatz.${grundsatz.schluessel}` as Sprachschluessel)}
      </h3>

      <p>
        {t(`pruefer.grundsatz.${grundsatz.schluessel}.${grundsatz.urteil}` as Sprachschluessel, {
          anzahl: anzahlwort(grundsatz.geprueft),
          fehler: anzahlwort(grundsatz.beanstandet),
          groesste: grundsatz.groesste === null ? '' : betrag(grundsatz.groesste),
        })}
      </p>

      <p className="erlaeuterung">
        {t(`pruefer.grundsatz.${grundsatz.schluessel}.was` as Sprachschluessel)}
      </p>

      {grundsatz.beispiele.length > 0 && (
        <p className="erlaeuterung">
          {t('pruefer.beispiele', { liste: grundsatz.beispiele.join(' · ') })}
        </p>
      )}

      {/*
        **Keine Liste der Regelnummern** (7.2 Abschnitt 34, R1133 — Entscheidung
        vom 2026-10-06, gegen die Empfehlung, die sie hier lassen wollte). Bis
        dahin stand hier „Dahinter stehen: R86, R802". Was die Regel sagt, steht
        im Satz darüber.
      */}
    </div>
  )
}

/**
 * Der Beispielkonzern und seine zehn Schritte.
 *
 * **Er bringt seine eigenen Bezeichnungen mit** (R807). Seine Konten heißen
 * nicht wie die des Hauses; wer sie im Katalog der Anlage nachschlüge, bekäme
 * eine fremde Bezeichnung oder gar keine. Deshalb bauen wir hier drei kleine
 * Nachschlagewerke und reichen sie überall dorthin, wo sonst der Katalog
 * gefragt würde.
 */
function Beispielkonzern({ lehrfall }: { lehrfall: Lehrfallbericht }) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('prueferansicht:beispielkonzern')

  const namen = useMemo(() => namensgeber(lehrfall), [lehrfall])

  return (
    <>
      <p className="erlaeuterung">{t('pruefer.lehrfall.erlaeuterung')}</p>
      <p className="erlaeuterung">
        {t('pruefer.lehrfall.periode', {
          periode: periodenname(lehrfall.periode),
          waehrung: lehrfall.konzernwaehrung,
        })}
      </p>

      {/*
        **Der Prüfcode steht am Bildschirm, im Ausdruck und in der Mappe**
        (R813) — drei Orte, eine Zahl. Mit dem Satz daneben, warum er sich
        ändert: Ohne ihn hält ihn der nächste Leser für einen Fehler.
      */}
      <p>
        {t('pruefer.pruefcode')}: <span className="hash">{lehrfall.pruefcode}</span>
      </p>
      <p className="erlaeuterung">{t('pruefer.pruefcode.was')}</p>

      <h3>{t('pruefer.lehrfall.gesellschaften')}</h3>
      <div className="tabellenrahmen">
        <table ref={spaltenrahmen} className="ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th>{t('pruefer.spalte.gesellschaft')}</th>
              <th>{t('pruefer.spalte.waehrung')}</th>
              <th className="zahl">{t('pruefer.spalte.anteil')}</th>
              <th className="zahl">{t('pruefer.spalte.kaufpreis')}</th>
              <th>{t('pruefer.spalte.erwerb')}</th>
            </tr>
          </thead>
          <tbody>
            {lehrfall.gesellschaften.map((g) => {
              const beteiligung = lehrfall.beteiligungen.find((b) => b.tochter === g.schluessel)
              return (
                <tr key={g.schluessel}>
                  <td>{beschriftung(g.namen)}</td>
                  <td>{g.waehrung}</td>
                  <td className="zahl">
                    {/* Wie im Register: „80 %", ein Drittel „33,3333 %" (R840). Über
                        `quote * 100` stand dort ein Drittel als 33.33333333 %. */}
                    {g.istObersteMutter || !beteiligung
                      ? t('pruefer.oberste-mutter')
                      : anteil(beteiligung.quote)}
                  </td>
                  <td className="zahl">
                    {beteiligung?.anschaffungskosten != null && <Betrag wert={beteiligung.anschaffungskosten} />}
                  </td>
                  <td>
                    {beteiligung?.erwerbsperiode == null ? '' : periodenname(beteiligung.erwerbsperiode)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <h3>{t('pruefer.lehrfall.ausgangslage')}</h3>
      <p className="erlaeuterung">{t('pruefer.lehrfall.ausgangslage.erlaeuterung')}</p>
      <Zeilentafel zeilen={lehrfall.ausgangslage} namen={namen} />

      <h3>{t('pruefer.lehrfall.schritte')}</h3>
      <p className="erlaeuterung">{t('pruefer.lehrfall.schritte.erlaeuterung')}</p>

      {lehrfall.schritte.map((schritt) => (
        <Schritt key={schritt.code} schritt={schritt} lehrfall={lehrfall} namen={namen} />
      ))}

      {/*
        **Das ungebaute Werkzeug steht in derselben Reihe** (R808). Es aus der
        Liste zu lassen hieße, die Frage nach den latenten Steuern gäbe es
        nicht — und der Prüfer stellt sie ohnehin.
      */}
      <details className="vorgang" id={KAPITEL.latenteSteuern}>
        <summary>
          <span className="nummer">—</span>
          <span className="name">{t('pruefer.latente-steuern')}</span>
          <span className="nummer">{t('pruefer.lehrfall.keine-buchung')}</span>
        </summary>
        <div style={{ padding: '10px 12px' }}>
          <p className="erlaeuterung">{t('pruefer.latente-steuern.regel')}</p>
        </div>
      </details>
    </>
  )
}

/**
 * Die Nachschlagewerke des Beispielkonzerns, gebündelt (R807).
 *
 * **Er bringt seine eigenen Bezeichnungen mit.** Seine Konten heißen nicht wie
 * die des Hauses; wer sie im Katalog der Anlage nachschlüge, bekäme eine
 * fremde Bezeichnung oder gar keine.
 *
 * **Eine Stelle für Bildschirm und Mappe.** Die Mappe trägt dieselben Sätze
 * wie der Bildschirm; zwei Namensgeber ergäben zwei Fassungen derselben
 * Bezeichnung.
 */
function namensgeber(lehrfall: Lehrfallbericht): Namensgeber {
  const konten = new Map(lehrfall.konten.map((k) => [k.schluessel, k]))
  const gesellschaften = new Map(lehrfall.gesellschaften.map((g) => [g.schluessel, g]))
  const bewegungsarten = new Map(lehrfall.bewegungsarten.map((b) => [b.schluessel, b]))

  const konto = (schluessel: string) => {
    const treffer = konten.get(schluessel)
    return treffer ? kontoname(treffer) : schluessel
  }

  const gesellschaft = (schluessel: string | null) => {
    if (!schluessel) {
      return '—'
    }

    const treffer = gesellschaften.get(schluessel)
    return treffer ? beschriftung(treffer.namen) : schluessel
  }

  const bewegungsart = (schluessel: string | null) => {
    if (!schluessel) {
      return '—'
    }

    // Kennt der Beispielkonzern sie nicht, ist es eine des Hauses — der
    // Vortrag etwa. Dann nennt sie der Katalog der Anlage; bis zum 2026-10-06
    // stand hier „BWA-VOR" (gefunden vom Wächter, R1220).
    const treffer = bewegungsarten.get(schluessel)
    return treffer ? beschriftung(treffer.namen) : bewegungsartname(schluessel)
  }

  /*
    **Jede Kennung, die in einer Herleitung stehen kann** (R792). Der
    Rechenkern schreibt Schlüssel, keine Namen — er kennt keine Sprache. Ohne
    diese Auflösung stünde in der Rechnung „LF-AU" statt „Auslandstochter
    Inc.".
  */
  const werk = new Map<string, string>()
  for (const [schluessel, g] of gesellschaften) {
    werk.set(schluessel, beschriftung(g.namen))
  }

  for (const [schluessel, b] of bewegungsarten) {
    werk.set(schluessel, beschriftung(b.namen))
  }

  for (const [schluessel, k] of konten) {
    werk.set(schluessel, kontoname(k))
  }

  werk.set(lehrfall.ledger, t('pruefer.lehrfall.ledger'))

  return { konto, gesellschaft, bewegungsart, kennung: (wert) => werk.get(wert) ?? wert }
}

/** Die Nachschlagewerke, wie die Bauteile sie hereingereicht bekommen. */
type Namensgeber = {
  konto: (schluessel: string) => string
  gesellschaft: (schluessel: string | null) => string
  bewegungsart: (schluessel: string | null) => string
  /** Löst jede Kennung auf, die in einer Herleitung stehen kann (R792). */
  kennung: (wert: string) => string
}

/** Gelieferte Zeilen — Ausgangslage wie Eigenkapital zum Erwerbstag. */
function Zeilentafel({ zeilen, namen }: { zeilen: Lehrfallzeile[]; namen: Namensgeber }) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('prueferansicht:zeilentafel')

  return (
    <div className="tabellenrahmen">
      <table ref={spaltenrahmen} className="ziehbaretabelle">
        <Spaltenmassleiste masse={spaltenmasse} />
        <thead>
          <tr>
            <th>{t('pruefer.spalte.gesellschaft')}</th>
            <th>{t('pruefer.spalte.konto')}</th>
            <th>{t('pruefer.spalte.bewegungsart')}</th>
            <th>{t('pruefer.spalte.partner')}</th>
            <th className="zahl">{t('pruefer.spalte.betrag')}</th>
            <th>{t('pruefer.spalte.waehrung')}</th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z, i) => (
            <tr key={i}>
              <td>{namen.gesellschaft(z.einheit)}</td>
              <td>{namen.konto(z.konto)}</td>
              <td>{namen.bewegungsart(z.bewegungsart)}</td>
              <td>{z.icPartner ? namen.gesellschaft(z.icPartner) : ''}</td>
              <td className={`zahl ${z.betrag < 0 ? 'minus' : ''}`}><Betrag wert={z.betrag} /></td>
              <td>{z.waehrung}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Eine Konsolidierungsart: die Regel, ihre Ausgangslage und die Buchungen.
 *
 * **Die Ausgangslage steht beim Schritt, nicht oben.** Die Kurse braucht nur
 * die Umrechnung, die Aufdeckung nur die Neubewertung. Oben gesammelt wären
 * es sechs Tafeln, von denen der Leser bei jeder fragt, wofür sie da ist.
 */
function Schritt({
  schritt,
  lehrfall,
  namen,
}: {
  schritt: Lehrfallschritt
  lehrfall: Lehrfallbericht
  namen: Namensgeber
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('prueferansicht:schritt')

  const name = schrittname(schritt)

  return (
    <details className="vorgang" id={schrittkennung(schritt.code)}>
      <summary>
        <span className="nummer">{schritt.reihenfolge}</span>
        <span className="name">{name}</span>
        <span className="nummer">
          {schritt.buchungen.length === 0
            ? t('pruefer.lehrfall.keine-buchung')
            : schritt.buchungen.length === 1
              ? t('pruefer.lehrfall.summe.eine')
              : t('pruefer.lehrfall.summe', { anzahl: anzahlwort(schritt.buchungen.length) })}
        </span>
      </summary>

      <div style={{ padding: '10px 12px' }}>
        <p className="erlaeuterung">
          {t(`pruefer.schritt.${schritt.code}.regel` as Sprachschluessel)}
        </p>

        <Ausgangslage code={schritt.code} lehrfall={lehrfall} namen={namen} />

        {schritt.meldungen.length > 0 && (
          <ul style={{ margin: '0 0 12px', paddingLeft: '18px' }} className="erlaeuterung">
            {schritt.meldungen.map((meldung, i) => (
              <li key={i}>{satz(meldung, namen.konto)}</li>
            ))}
          </ul>
        )}

        {/*
          **Ein Werkzeug ohne Buchung sagt das** (R808). Eine leere Fläche
          sähe aus wie „wurde nicht gerechnet"; der Satz sagt, daß gerechnet
          wurde und nichts zu tun war.
        */}
        {schritt.buchungen.length === 0 ? (
          <p className="hinweis">{t('pruefer.lehrfall.ohne-buchung')}</p>
        ) : (
          <div className="tabellenrahmen">
            <table ref={spaltenrahmen} className="ziehbaretabelle">
              <Spaltenmassleiste masse={spaltenmasse} />
              <thead>
                <tr>
                  <th>{t('pruefer.spalte.gesellschaft')}</th>
                  <th>{t('pruefer.spalte.konto')}</th>
                  <th>{t('pruefer.spalte.bewegungsart')}</th>
                  <th className="zahl">{t('pruefer.spalte.betrag')}</th>
                  <th>{t('pruefer.spalte.rechnung')}</th>
                </tr>
              </thead>
              <tbody>
                {schritt.buchungen.map((buchung) => (
                  <tr key={buchung.nummer}>
                    <td>{namen.gesellschaft(buchung.einheit)}</td>
                    <td>{namen.konto(buchung.konto)}</td>
                    <td>{namen.bewegungsart(buchung.bewegungsart)}</td>
                    <td className={`zahl ${buchung.betrag < 0 ? 'minus' : ''}`}>
                      <Betrag wert={buchung.betrag} />
                    </td>
                    <td>
                      <div>{regelname(buchung.regel)}</div>
                      <div className="erlaeuterung">{satz(buchung.erlaeuterung, namen.konto)}</div>
                      {buchung.herleitung.length > 0 && (
                        <dl className="herleitung">
                          {buchung.herleitung.map((teil, i) => (
                            <div key={i} style={{ display: 'contents' }}>
                              <dt>{herleitungsname(teil.feld)}</dt>
                              <dd>
                                {teil.art === 'text'
                                  ? namen.kennung(teil.wert)
                                  : schrittwert(teil, namen.konto)}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </details>
  )
}

/**
 * Die Eingangswerte, die genau dieser Schritt liest.
 *
 * **Jede Tafel beantwortet eine Frage, sonst steht sie nicht da.** Wer die
 * Umrechnung liest, will die Kurse sehen; wer die Kapitalkonsolidierung liest,
 * das Eigenkapital vom Erwerbstag. Dieselbe Tafel unter jedem Schritt wäre
 * fünfmal Beiwerk und einmal die Antwort.
 */
function Ausgangslage({
  code,
  lehrfall,
  namen,
}: {
  code: string
  lehrfall: Lehrfallbericht
  namen: Namensgeber
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse1, rahmen: spaltenrahmen1 } =
    useSpaltenmasse<HTMLTableElement>('prueferansicht:ausgangslage:1')

  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse3, rahmen: spaltenrahmen3 } =
    useSpaltenmasse<HTMLTableElement>('prueferansicht:ausgangslage:3')

  if (code === 'umrechnung') {
    return (
      <>
        <h4>{t('pruefer.tafel.kurse')}</h4>
        <div className="tabellenrahmen">
          <table ref={spaltenrahmen1} className="ziehbaretabelle">
            <Spaltenmassleiste masse={spaltenmasse1} />
            <thead>
              <tr>
                <th>{t('pruefer.spalte.kursart')}</th>
                <th>{t('pruefer.spalte.waehrung')}</th>
                <th className="zahl">{t('pruefer.spalte.kurs')}</th>
              </tr>
            </thead>
            <tbody>
              {lehrfall.kurse.map((k) => (
                <tr key={k.art}>
                  {/*
                    **Die Kursarten sind schon beschriftet** — im Wertevorrat
                    der Stammdatenpflege. Eine zweite Beschriftung hier hieße,
                    daß derselbe Kurs auf zwei Bildschirmen anders heißt (R801).
                  */}
                  <td>{t(`stammdaten.wert.${k.art.toLowerCase()}` as Sprachschluessel)}</td>
                  <td>
                    {k.waehrung} / {k.gegenwaehrung}
                  </td>
                  <td className="zahl">{kurswert(k.wert)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )
  }

  if (code.startsWith('zwischenergebniseliminierung')) {
    return (
      <>
        <h4>{t('pruefer.tafel.zwischenergebnisse')}</h4>
        <div className="tabellenrahmen">
          <table ref={spaltenrahmen3} className="ziehbaretabelle">
            <Spaltenmassleiste masse={spaltenmasse3} />
            <thead>
              <tr>
                <th>{t('pruefer.spalte.gesellschaft')}</th>
                <th>{t('pruefer.spalte.konto')}</th>
                <th>{t('pruefer.spalte.lieferant')}</th>
                <th className="zahl">{t('pruefer.spalte.bestand')}</th>
                <th className="zahl">{t('pruefer.spalte.zwischengewinn')}</th>
              </tr>
            </thead>
            <tbody>
              {lehrfall.zwischenergebnisse.map((z, i) => (
                <tr key={i}>
                  <td>{namen.gesellschaft(z.einheit)}</td>
                  <td>{namen.konto(z.konto)}</td>
                  <td>{namen.gesellschaft(z.lieferant)}</td>
                  <td className="zahl"><Betrag wert={z.bestand} /></td>
                  <td className="zahl"><Betrag wert={z.betrag} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )
  }

  if (code === 'kapitalkonsolidierung') {
    return (
      <>
        <h4>{t('pruefer.tafel.einbezug')}</h4>
        <Zeilentafel zeilen={lehrfall.eigenkapitalZumEinbezug} namen={namen} />
      </>
    )
  }

  return null
}

/**
 * Die Mappe für die Prüfungsakte — drei Blätter (2.13 Abschnitt 9.11).
 *
 * **Grundsätze, Ausgangslage, Buchungen.** Nicht ein Blatt je
 * Konsolidierungsart: Über alle Buchungen zu filtern und zu summieren ist
 * genau das, was ein Prüfer in Excel tut, und zwölf Blätter nähmen ihm das ab.
 *
 * **Die Sätze sind dieselben wie am Bildschirm.** Sie entstehen hier, in der
 * Sprachdatei, und werden dem Server fertig übergeben (R157). Eine zweite
 * Stelle, die denselben Satz baut, wäre eine zweite Wahrheit.
 *
 * **Jedes Blatt trägt den Prüfcode im Briefkopf** (R813). Damit ist die Mappe
 * dasselbe Dokument wie der Ausdruck und der Bildschirm — und nicht eine
 * dritte Auskunft.
 */
function mappe(bericht: Regelnachweis): Mappenauftrag {
  const lehrfall = bericht.lehrfall
  const namen = namensgeber(lehrfall)
  const heute = new Date()

  const briefkopf = (titel: string) => [
    titel,
    t('pruefer.mappe.erstellt', { datum: tagesdatum(heute.toISOString()) }),
    t('pruefer.mappe.pruefcode', { code: lehrfall.pruefcode }),
  ]

  return {
    dateiname: t('pruefer.mappe.datei', { datum: heute.toISOString().slice(0, 10) }),
    blaetter: [
      {
        name: t('pruefer.mappe.blatt.grundsaetze'),
        briefkopf: briefkopf(t('pruefer.mappe.titel.grundsaetze')),
        kopf: [
          t('pruefer.mappe.spalte.grundsatz'),
          t('pruefer.mappe.spalte.urteil'),
          t('pruefer.mappe.spalte.geprueft'),
          t('pruefer.mappe.spalte.beanstandet'),
          t('pruefer.mappe.spalte.fundstellen'),
          // Die Spalte der Regelnummern ist entfallen (R1133).
        ],
        zeilen: [
          ...bericht.grundsaetze.map((g) => [
            text(t(`pruefer.grundsatz.${g.schluessel}` as Sprachschluessel)),
            text(t(`pruefer.urteil.${g.urteil}` as Sprachschluessel)),
            ganz(g.geprueft),
            ganz(g.beanstandet),
            text(g.beispiele.join(' · ')),
          ]),

          // **Die ungeprüften stehen auf demselben Blatt** (R805). Auf einem
          // eigenen sähe die Liste der Grundsätze vollständig aus, und genau
          // das ist sie nicht.
          ...bericht.ungeprueft.map((u) => [
            text(t(`pruefer.ungeprueft.${u.schluessel}` as Sprachschluessel)),
            text(t('pruefer.urteil.ungeprueft')),
            text(''),
            text(''),
            text(''),
          ]),
        ],
      },
      {
        name: t('pruefer.mappe.blatt.ausgangslage'),
        briefkopf: briefkopf(t('pruefer.mappe.titel.ausgangslage')),
        kopf: [
          t('pruefer.mappe.spalte.herkunft'),
          t('pruefer.spalte.gesellschaft'),
          t('pruefer.spalte.konto'),
          t('pruefer.spalte.bewegungsart'),
          t('pruefer.spalte.partner'),
          t('pruefer.spalte.betrag'),
          t('pruefer.spalte.waehrung'),
        ],
        zeilen: [
          ...lehrfall.ausgangslage.map((z) =>
            ausgangszeile(t('pruefer.mappe.herkunft.lieferung'), z, namen),
          ),

          // **Das Eigenkapital vom Erwerbstag gehört dazu**, sonst läßt sich
          // der Firmenwert auf Blatt drei nicht nachrechnen — und dafür ist
          // die Mappe da.
          ...lehrfall.eigenkapitalZumEinbezug.map((z) =>
            ausgangszeile(t('pruefer.mappe.herkunft.einbezug'), z, namen),
          ),
        ],
      },
      {
        name: t('pruefer.mappe.blatt.buchungen'),
        briefkopf: briefkopf(t('pruefer.mappe.titel.buchungen')),
        kopf: [
          t('pruefer.mappe.spalte.art'),
          t('pruefer.mappe.spalte.nr'),
          t('pruefer.spalte.gesellschaft'),
          t('pruefer.spalte.konto'),
          t('pruefer.spalte.bewegungsart'),
          t('pruefer.spalte.betrag'),
          t('pruefer.spalte.waehrung'),
          t('pruefer.mappe.spalte.regel'),
          t('pruefer.mappe.spalte.erlaeuterung'),
          t('pruefer.spalte.rechnung'),
        ],
        zeilen: lehrfall.schritte.flatMap((schritt) =>
          schritt.buchungen.length === 0
            ? // **Ein Werkzeug ohne Buchung sagt das auch in der Mappe**
              // (R808). Eine fehlende Zeile sähe aus wie ein Werkzeug, das es
              // nicht gibt.
              [
                [
                  text(schrittname(schritt)),
                  text(''),
                  text(''),
                  text(''),
                  text(''),
                  text(''),
                  text(''),
                  text(''),
                  text(schritt.meldungen.map((m) => satz(m, namen.konto)).join(' ')),
                  text(''),
                ],
              ]
            : schritt.buchungen.map((b) => [
                text(schrittname(schritt)),
                ganz(b.nummer),
                text(namen.gesellschaft(b.einheit)),
                text(namen.konto(b.konto)),
                text(namen.bewegungsart(b.bewegungsart)),
                zahl(b.betrag),
                text(b.waehrung),
                text(regelname(b.regel)),
                text(satz(b.erlaeuterung, namen.konto)),
                text(rechnung(b.herleitung, namen)),
              ]),
        ),
      },
    ],
  }
}

/** Eine Zeile der Ausgangslage, mit der Angabe, woher sie stammt. */
function ausgangszeile(herkunft: string, z: Lehrfallzeile, namen: Namensgeber): Mappenzelle[] {
  return [
    text(herkunft),
    text(namen.gesellschaft(z.einheit)),
    text(namen.konto(z.konto)),
    text(namen.bewegungsart(z.bewegungsart)),
    text(z.icPartner ? namen.gesellschaft(z.icPartner) : ''),
    zahl(z.betrag),
    text(z.waehrung),
  ]
}

/**
 * Die Rechnung hinter einer Buchung als **ein** Text.
 *
 * **Und nicht als eigene Spalten** (Entscheidung vom 2026-09-26). Eine
 * Herleitung ist mal drei, mal sechs Angaben lang; eigene Spalten hießen, die
 * Blattbreite nach dem längsten Fall zu bemessen und in jedem anderen Fall
 * Leere zu drucken. Der Preis ist benannt: Nach einer einzelnen Größe läßt
 * sich an dieser Spalte nicht filtern.
 */
function rechnung(
  herleitung: { feld: string; wert: string; art: string }[],
  namen: Namensgeber,
): string {
  return herleitung
    .map(
      (teil) =>
        `${herleitungsname(teil.feld)}: ${
          teil.art === 'text'
            ? namen.kennung(teil.wert)
            : schrittwert(teil as never, namen.konto)
        }`,
    )
    .join(' · ')
}
